import { useEffect } from "react";
import toast from "react-hot-toast";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { glencocoClientAPI } from "@/api/glencoco";
import { webAIClientStreamAPI } from "@/api/webai";
import {
  CreateEmailSequenceRequestParamsI,
  CreateEmailTemplateRequestParamsI,
  GetEmailGenerationInfoRequestParamsI,
  SendEmailRequestParamsI,
  SendEmailSequenceRequestParamsI,
  UpdateEmailTemplateRequestParamsI,
} from "@/api/routes/email";

import { GenerateEmailStreamRequestParamsI } from "shared/lib/interfaces/email/ai";
import { EmailTemplatesSidebarQueryKeys } from "@/modules/email-templates-sidebar/keys";
import { useApiClient } from "@/context/api-client";

// Get all lists
export const useFetchEmailTemplatesList = () => {
  const listApi = useQuery({
    queryKey: EmailTemplatesSidebarQueryKeys.list,
    queryFn: glencocoClientAPI().email.getEmailTemplatesList,
  });

  useEffect(() => {
    if (listApi.isError) {
      toast.error(
        "Failed to load list of templates, please try reloading the page."
      );
    }
  }, [listApi.isError]);

  return listApi;
};

export const useCheckCampaignHasEmailAutomation = (campaignId?: string) =>
  useQuery({
    queryKey: EmailTemplatesSidebarQueryKeys.campaignEmailAutomationCheck,
    queryFn: () =>
      campaignId
        ? glencocoClientAPI().email.checkCampaignHasEmailAutomation(campaignId)
        : null,
  });

/**
 * Returns the information needed to generate an email
 * (returns an id that is needed for submitting the email later)
 **/
export const useEmailGenerationInfo = () => {
  return useMutation({
    mutationFn: ({
      templateId,
      parameters,
    }: {
      templateId?: string;
      parameters?: GetEmailGenerationInfoRequestParamsI;
    }) =>
      glencocoClientAPI().email.getEmailGenerationInfo(templateId, parameters),
    onError: () => toast.error("Failed to get email configurations."),
  });
};

export const useGenerateAIEmailStreamBody = () => {
  return useMutation({
    mutationFn: ({
      parameters,
      signal,
    }: {
      parameters: GenerateEmailStreamRequestParamsI;
      signal?: AbortSignal;
    }) => webAIClientStreamAPI().streamEmailBody(parameters, signal),
    onError: () => toast.error("Failed to generate AI email."),
  });
};

export const useGenerateAIEmailStreamSubject = () => {
  return useMutation({
    mutationFn: ({
      parameters,
      signal,
    }: {
      parameters: GenerateEmailStreamRequestParamsI;
      signal?: AbortSignal;
    }) => webAIClientStreamAPI().streamEmailSubject(parameters, signal),
    onError: () => toast.error("Failed to generate AI email."),
  });
};

export const useValidateEmails = () => {
  return useMutation({
    mutationFn: (emails: string[]) =>
      glencocoClientAPI().email.validateEmails({ emails }),
  });
};

export const useQuerySendEmail = () => {
  return useMutation({
    mutationFn: ({ parameters }: { parameters: SendEmailRequestParamsI }) =>
      glencocoClientAPI().email.sendEmail(parameters),
    onError: () => {
      toast.error("Failed to send email");
    },
  });
};

// List CRUD
export const useQueryCreateEmailTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (parameters: CreateEmailTemplateRequestParamsI) =>
      glencocoClientAPI().email.createEmailTemplate(parameters),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EmailTemplatesSidebarQueryKeys.list,
      });
    },
    onError: () => toast.error("Failed to create new template."),
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      params,
    }: {
      templateId: string;
      params: UpdateEmailTemplateRequestParamsI;
    }) => glencocoClientAPI().email.updateEmailTemplate(templateId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EmailTemplatesSidebarQueryKeys.list,
      });
    },
    onError: () => toast.error("Failed to update template."),
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) =>
      glencocoClientAPI().email.deleteEmailTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EmailTemplatesSidebarQueryKeys.list,
      });
    },
    onError: () => toast.error("Failed to remove template."),
  });
};

// Get email history
export const useFetchEmailHistoryQuery = (
  campaignId: string | undefined,
  contactId: string | undefined
) => {
  const api = useApiClient();

  const emailHistoryQuery = useInfiniteQuery({
    enabled: !!campaignId && !!contactId,
    queryKey: EmailTemplatesSidebarQueryKeys.emailHistory,
    queryFn: ({ pageParam }) =>
      api.email.getEmailHistory(campaignId as string, contactId as string, {
        next_token: pageParam,
      }),
    select: (data) => data?.pages.flatMap((page) => page.data.emails) || [],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage?.data?.next_token,
  });

  useEffect(() => {
    if (emailHistoryQuery.isError) {
      toast.error("Failed to load email history.");
    }
  }, [emailHistoryQuery.isError]);

  return emailHistoryQuery;
};

export const useCreateEmailSequence = () => {
  const queryClient = useQueryClient();
  const API = useApiClient();

  return useMutation({
    mutationFn: (params: CreateEmailSequenceRequestParamsI) =>
      API.email.createEmailSequence(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EmailTemplatesSidebarQueryKeys.emailSequence,
      });
    },
    onError: () => toast.error("Failed to create email sequence."),
  });
};

export const useFetchEmailSequence = (sequenceId: string) => {
  return useQuery({
    queryKey: EmailTemplatesSidebarQueryKeys.emailSequence,
    queryFn: () =>
      sequenceId
        ? glencocoClientAPI().email.getEmailSequence(sequenceId)
        : null,
  });
};

export const useFetchEmailSequenceById = () => {
  const queryClient = useQueryClient();
  const API = useApiClient();

  return useMutation({
    mutationFn: (sequenceId: string) => API.email.getEmailSequence(sequenceId),
    onSuccess: (data) => {
      queryClient.setQueryData(
        EmailTemplatesSidebarQueryKeys.emailSequence,
        data
      );
    },
    onError: () => toast.error("Failed to fetch email sequence."),
  });
};

export const useAddEmailToSequence = () => {
  const queryClient = useQueryClient();
  const API = useApiClient();

  return useMutation({
    mutationFn: (sequenceId: string) =>
      API.email.addEmailToSequence(sequenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EmailTemplatesSidebarQueryKeys.emailSequence,
      });
    },
    onError: () => toast.error("Failed to add email to sequence."),
  });
};

export const useRemoveEmailFromSequence = () => {
  const queryClient = useQueryClient();
  const API = useApiClient();

  return useMutation({
    mutationFn: (emailId: string) => API.email.removeEmailFromSequence(emailId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EmailTemplatesSidebarQueryKeys.emailSequence,
      });
    },
    onError: () => toast.error("Failed to remove email from sequence."),
  });
};

export const useQuerySendEmailSequence = () => {
  const API = useApiClient();

  return useMutation({
    mutationFn: ({
      sequenceId,
      parameters,
    }: {
      sequenceId: string;
      parameters: SendEmailSequenceRequestParamsI;
    }) => API.email.sendEmailSequence(sequenceId, parameters),
    onError: () => toast.error("Failed to send email sequence."),
  });
};
